// src/components/LlmProcessingCard/LlmProcessingCard.js
import React, { useState } from "react";
import {
  Paper,
  Box,
  Typography,
  Button,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableContainer,
  Collapse,
} from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import VerifiedUserOutlinedIcon from "@mui/icons-material/VerifiedUserOutlined";

import InputIcon from "@mui/icons-material/Input";
import PsychologyIcon from "@mui/icons-material/Psychology";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import OutputIcon from "@mui/icons-material/Output";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";

import LlmFlowDiagram from "./LlmFlowDiagram";
import AiBorderWrapper from "../AIBorderWrapper";
import {
  LLM_GENERIC_FLOW_BLOCKS,
  LLM_GENERIC_FLOW_DETAILS_CONFIG,
} from "../../config/t4rConfig";

/**
 * LlmProcessingCard
 *
 * Props:
 *  - loading: boolean
 *  - onProceed: () => void
 *  - concepts?: { name: string; description: string }[]
 *  - disclaimerText?: string
 *  - flowBlocks?: BlockConfig[]
 *  - flowDetailsConfig?: DetailsConfig
 */
export default function LlmProcessingCard({
  loading,
  onProceed,
  concepts = [],
  disclaimerText,
  flowBlocks,
  flowDetailsConfig,
}) {
  const [showConcepts, setShowConcepts] = useState(false);
  const [showFlowDetails, setShowFlowDetails] = useState(false);

  const effectiveDisclaimer =
    disclaimerText ||
    "This tool uses an AI model to suggest learning journeys based on your inputs. " +
      "The model can make mistakes or overlook important context. Please review all outputs carefully — " +
      "you remain responsible for any decisions or actions based on these results.";

  const hasConcepts = Array.isArray(concepts) && concepts.length > 0;

  // Use step-specific config if provided, otherwise fall back to generic T4R overview
  const effectiveFlowBlocks = flowBlocks || LLM_GENERIC_FLOW_BLOCKS;
  const effectiveFlowDetailsConfig =
    flowDetailsConfig || LLM_GENERIC_FLOW_DETAILS_CONFIG;

  // Mini one-line block style (for collapsed summary)
  const miniBlockSx = (variant) => {
    const isAi = variant === "ai";
    const isIo = variant === "io"; // input/output
    return {
      display: "flex",
      alignItems: "center",
      gap: 0.5,
      px: { xs: 1, sm: 1.2 },
      py: 0.4,
      borderRadius: 999,
      fontSize: { xs: "0.68rem", sm: "0.72rem" },
      fontWeight: 500,
      bgcolor: isAi ? "primary.main" : isIo ? "grey.200" : "grey.100",
      color: isAi ? "common.white" : "text.primary",
      whiteSpace: "nowrap",
      flexShrink: 0,
    };
  };

  // Comma-separated concept names (for the concepts summary line)
  const conceptNames = hasConcepts
    ? concepts.map((c) => c.name).join(", ")
    : "";

  // Single handler for Proceed: reset internal UI, then notify parent
  const handleProceedClick = () => {
    setShowFlowDetails(false);
    setShowConcepts(false);
    if (onProceed) {
      onProceed();
    }
  };

  return (
    <AiBorderWrapper
      aiActive={true} // always show AI border
      animated={!!loading} // shimmer only while loading
    >
      <Paper
        variant="outlined"
        sx={{
          p: 3,
          borderRadius: 2,
          border: "none", // border handled by AiBorderWrapper
          backgroundColor: "background.paper",
        }}
      >
        {/* Top status: icon + main message, centred */}
        <Box
          sx={{
            textAlign: "center",
            mb: 2,
          }}
        >
          {loading ? (
            <CircularProgress size={40} />
          ) : (
            <CheckCircleOutlineIcon color="success" sx={{ fontSize: 40 }} />
          )}

          <Typography
            variant="h6"
            sx={{
              mt: 2,
              fontWeight: 600,
            }}
          >
            The Agent is talking to an LLM to prepare a suggested learning
            journey.
          </Typography>
        </Box>

        {/* AI disclaimer */}
        <Box sx={{ mt: 2 }}>
          <Paper
            elevation={0}
            sx={{
              p: 2,
              borderRadius: 2,
              bgcolor: "rgba(0, 0, 0, 0.03)",
              borderLeft: "4px solid",
              borderColor: "grey.600",
              display: "flex",
              alignItems: "flex-start",
              gap: 1.5,
            }}
          >
            <VerifiedUserOutlinedIcon
              sx={{
                fontSize: 24,
                color: "grey.700",
                mt: 0.2,
              }}
            />
            <Typography variant="body2" color="text.secondary">
              {effectiveDisclaimer}
            </Typography>
          </Paper>
        </Box>

        {/* Pipeline summary + expandable detailed block diagram */}
        <Box sx={{ mt: 3 }}>
          <Paper
            variant="outlined"
            sx={{
              p: 2,
              borderRadius: 2,
              bgcolor: "grey.50",
            }}
          >
            {/* Pipeline summary (hidden when expanded) + toggle */}
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 1.5,
              }}
            >
              {/* Mini pipeline flow — only visible when collapsed */}
              {!showFlowDetails && (
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: { xs: "column", sm: "row" },
                    alignItems: "center",
                    justifyContent: "center",
                    gap: { xs: 0.5, sm: 1 },
                    flexGrow: 1,
                    minWidth: 0,
                    py: { xs: 1, sm: 0 },
                    overflowX: { sm: "auto" },
                  }}
                >
                  <Box sx={miniBlockSx("io")}>
                    <InputIcon fontSize="inherit" />
                    <span>Inputs</span>
                  </Box>

                  <ArrowForwardIcon
                    sx={{
                      fontSize: 18,
                      color: "text.disabled",
                      transform: { xs: "rotate(90deg)", sm: "none" },
                    }}
                  />

                  <Box sx={miniBlockSx("ai")}>
                    <PsychologyIcon fontSize="inherit" />
                    <span>Input Processor</span>
                  </Box>

                  <ArrowForwardIcon
                    sx={{
                      fontSize: 18,
                      color: "text.disabled",
                      transform: { xs: "rotate(90deg)", sm: "none" },
                    }}
                  />

                  <Box sx={miniBlockSx("ai")}>
                    <AutoAwesomeIcon fontSize="inherit" />
                    <span>LLM (Mistral)</span>
                  </Box>

                  <ArrowForwardIcon
                    sx={{
                      fontSize: 18,
                      color: "text.disabled",
                      transform: { xs: "rotate(90deg)", sm: "none" },
                    }}
                  />

                  <Box sx={miniBlockSx("ai")}>
                    <PsychologyIcon fontSize="inherit" />
                    <span>Output Processor</span>
                  </Box>

                  <ArrowForwardIcon
                    sx={{
                      fontSize: 18,
                      color: "text.disabled",
                      transform: { xs: "rotate(90deg)", sm: "none" },
                    }}
                  />

                  <Box sx={miniBlockSx("io")}>
                    <OutputIcon fontSize="inherit" />
                    <span>Outputs</span>
                  </Box>
                </Box>
              )}

              {/* Toggle button for full explanation */}
              <Button
                size="small"
                variant="text"
                onClick={() => setShowFlowDetails((prev) => !prev)}
                startIcon={
                  showFlowDetails ? <ExpandLessIcon /> : <ExpandMoreIcon />
                }
                sx={{ textTransform: "none", whiteSpace: "nowrap" }}
              >
                {showFlowDetails
                  ? "Hide system explanation"
                  : "Show system explanation"}
              </Button>
            </Box>

            {/* Expanded content: full interactive flow diagram + details */}
            <Collapse in={showFlowDetails} unmountOnExit>
              <Box
                sx={{
                  mt: 2,
                  width: "100%",
                  maxWidth: "100%",
                  overflowX: "hidden",
                }}
              >
                <LlmFlowDiagram
                  loading={loading}
                  blocks={effectiveFlowBlocks}
                  detailsConfig={effectiveFlowDetailsConfig}
                />
              </Box>
            </Collapse>
          </Paper>
        </Box>

        {/* Concepts summary + collapsible details */}
        {hasConcepts && (
          <Box sx={{ mt: 2 }}>
            <Paper
              variant="outlined"
              sx={{
                p: 2,
                borderRadius: 2,
                bgcolor: "grey.50",
              }}
            >
              {/* Top row: comma-separated concept names + toggle */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  flexWrap: "wrap",
                  gap: 1.5,
                }}
              >
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{
                    flexGrow: 1,
                    minWidth: 0,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                  title={conceptNames}
                >
                  {conceptNames}
                </Typography>

                <Button
                  size="small"
                  variant="text"
                  onClick={() => setShowConcepts((prev) => !prev)}
                  startIcon={
                    showConcepts ? <ExpandLessIcon /> : <ExpandMoreIcon />
                  }
                  sx={{ textTransform: "none", whiteSpace: "nowrap" }}
                >
                  {showConcepts
                    ? "Hide conceptual details"
                    : "Show conceptual details"}
                </Button>
              </Box>

              {/* Expanded details: full concept table */}
              <Collapse in={showConcepts} unmountOnExit>
                <TableContainer
                  sx={{
                    mt: 1.5,
                    borderRadius: 1,
                    border: "1px solid",
                    borderColor: "grey.200",
                  }}
                >
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell
                          sx={{
                            fontWeight: 600,
                            bgcolor: "grey.100",
                            width: "30%",
                          }}
                        >
                          Concept
                        </TableCell>
                        <TableCell
                          sx={{
                            fontWeight: 600,
                            bgcolor: "grey.100",
                            width: "70%",
                          }}
                        >
                          How it supports the prediction
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {concepts.map((c) => (
                        <TableRow key={c.name}>
                          <TableCell sx={{ fontWeight: 500 }}>
                            {c.name}
                          </TableCell>
                          <TableCell>{c.description}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Collapse>
            </Paper>
          </Box>
        )}

        {/* Proceed button */}
        <Box sx={{ mt: 3, textAlign: "right" }}>
          <Button
            variant="contained"
            onClick={handleProceedClick}
            disabled={loading}
          >
            Proceed
          </Button>
        </Box>
      </Paper>
    </AiBorderWrapper>
  );
}
